/**
 * "Send from Content Library" — de mediakiezer in het chatvenster.
 *
 * Gemeten aan oud (SweetSilina / google1780445292, viewport 1920×1080):
 *
 *   dialoog        900×1016 op x=510
 *   kaart          200×300, vier per rij
 *   endpoints      GET /api/admin/content/folders?model_slug=…
 *                  GET /api/admin/content?model_slug=…&limit=48&offset=…
 *                            &conversation_id=…[&folder_id=…]
 *                  POST /api/admin/conversations/{id}/send-library-content
 *
 * Eén verschil met oud, met opzet: de miniaturen komen uit de servercache
 * (`/content/thumbnail/{id}`) in plaats van het volledige bestand
 * (`/content/file/{id}`). Oud haalde per kaart de hele foto op — bij 48
 * kaarten is dat tientallen megabytes over de NAS, terwijl de cache er
 * ongeveer 12 kB per stuk van maakt. Klikken op het oog laat wel het echte
 * bestand zien.
 *
 * De prijs bij "Paid" staat op FanCentro in CREDITS en op F2F in euro's. Dat
 * is geen detail: 800 credits is €8, en wie daar een euroteken voor zet maakt
 * er een bijlage van achthonderd euro van.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { Modal } from './Modal'
import { EmojiKnop } from './EmojiKnop'
import { VerzendPreview } from './VerzendPreview'
import type { PreviewItem } from './VerzendPreview'
import { duurLabel } from '../lib/duur'
import { Loading, Tag } from './ui'
import { api, fileUrl, getMetBladeren, thumbUrl } from '../lib/api'
import { useBijRand, useInBeeld, warmBeelden } from '../lib/scrollLaden'
import { useActions } from '../lib/actions'
import { credits, num } from '../lib/format'
import {
  bibliotheekVoorstel, kiezerPrijsFout, MASS_PPV_MIN_F2F_EUR, MASS_PPV_MIN_FC_CREDITS, PPV_MIN_F2F_EUR, PPV_MIN_FC_CREDITS,
  prijsHulpTekst,
} from '../lib/massPrijs'

interface Map_ {
  id: number
  name: string
  path: string
  item_count: number
}

interface Item {
  id: number
  title: string
  content_type: string
  file_reference: string | null
  base_price: number
  tags: string[]
  already_sent: boolean
  /** 14-10-2026: duur van een video in seconden; leeg = nog niet gemeten. */
  duration_seconds?: number | null
}

/** Een verzendopdracht zoals `media-send-jobs/mine` hem teruggeeft. */
interface Taak {
  id: string
  status: 'queued' | 'running' | 'succeeded' | 'failed' | string
  error: string | null
  message_ids: number[]
}

/** Hoe lang het venster op de uitkomst wacht, en hoe vaak het peilt. */
const VOLG_MAX_MS = 60_000
const VOLG_TIK_MS = 1_500

const SOORTEN = [
  { value: 'all', label: 'All' },
  { value: 'photo', label: 'Photos' },
  { value: 'video', label: 'Videos' },
  { value: 'audio', label: 'Audio' },
]

const PER_PAGINA = 48
/** Hoeveel miniaturen van de volgende bladzijde tegelijk alvast worden opgehaald. */
const WARM_TEGELIJK = 3
/** Wacht even met klaarzetten, zodat eerst wat in beeld staat binnenkomt. */
const WARM_NA_MS = 1500

interface Bladzijde {
  offset: number
  gen: number
  rij: Item[]
  volgende: number
  meer: boolean
}

/**
 * De miniatuur van één kaart, pas opgehaald als hij in de buurt van het
 * zichtbare deel van het raster komt (30-09-2026). `loading="lazy"` alleen
 * haalde 44 van de 48 meteen op: de browser meet vanaf het hele venster, niet
 * vanaf het scrollvak van het raster.
 */
function KiezerBeeld({ id, titel, raster }: {
  id: number
  titel: string
  raster: RefObject<HTMLDivElement | null>
}) {
  const el = useRef<HTMLDivElement>(null)
  const zichtbaar = useInBeeld(el, raster, '200px 0px')
  return (
    <div ref={el} className="kiezerbeeld">
      {zichtbaar ? <img src={thumbUrl(id)} alt={titel} loading="lazy" /> : null}
    </div>
  )
}

/** De ondergrens voor betaalde content komt uit lib/massPrijs.ts (één plek;
 *  05-10-2026, Jordy: ook een privébericht minstens 500 credits / €5). */
export { MASS_PPV_MIN_FC_CREDITS, MASS_PPV_MIN_F2F_EUR } from '../lib/massPrijs'

/** Het pad van een item zonder de model- en agencymap ervoor. */
function mapPad(ref: string | null): string {
  if (!ref) return '—'
  const delen = ref.split('/').filter(Boolean)
  // "Nova Temptation / <model> / Photo / Naked / Ass / bestand.jpg"
  return delen.slice(2, -1).join(' / ') || '—'
}

/** Wat de kiezer in kiesmodus teruggeeft (Mass DM, 01-10-2026). */
export interface ContentKeuze {
  /** 04-10-2026: `base_price` uit de bibliotheek, als voorstel voor de prijs. */
  items: { id: number; title: string; content_type: string | null; base_price?: number | null }[]
  betaald: boolean
  prijs: string
}

export function ContentPicker({ conversationId, modelSlug, platform, onClose, onVerstuurd, kiesModus }: {
  /** Het gesprek waarnaar verstuurd wordt; leeg in kiesmodus. */
  conversationId?: number
  modelSlug: string
  platform: string
  onClose: () => void
  onVerstuurd?: () => void
  /**
   * 01-10-2026 (Jordy): dezelfde kiezer voor Mass DM. In kiesmodus verstuurt
   * hij niets: je kiest content en gratis/betaald met een prijs, en
   * `onKies` geeft dat terug aan het Mass DM-venster. Geen bijschrift (dat
   * heeft de Mass DM zelf) en geen "al naar deze fan"-slot (er is geen fan).
   */
  kiesModus?: {
    max: number
    begin?: ContentKeuze
    magBetaald?: boolean
    onKies: (keuze: ContentKeuze) => void
  }
}) {
  const { notify } = useActions()
  const f2f = String(platform).toLowerCase() === 'f2f'

  const [soort, setSoort] = useState('all')
  const [map, setMap] = useState<number | null>(null)
  const [zoek, setZoek] = useState('')
  const [zoekTraag, setZoekTraag] = useState('')
  const [mappen, setMappen] = useState<Map_[]>([])
  const [items, setItems] = useState<Item[]>([])
  const [laadt, setLaadt] = useState(true)
  const [meer, setMeer] = useState(false)
  const [volgende, setVolgende] = useState(0)
  // 30-09-2026: automatisch doorladen. Een filterwissel begint een nieuwe
  // generatie; een antwoord van een oudere generatie wordt weggegooid, zodat
  // een late bladzijde van "Photos" nooit in de lijst van "Videos" belandt.
  const generatie = useRef(0)
  const onderweg = useRef(false)
  const voorraad = useRef<Bladzijde | null>(null)
  /** Een bladzijde die al wordt klaargezet: wie hem nodig heeft, wacht erop. */
  const klaarzetten = useRef<{ offset: number; gen: number; belofte: Promise<Bladzijde | null> } | null>(null)
  const volgendeRef = useRef(0)
  const raster = useRef<HTMLDivElement>(null)
  const wacht = useRef<HTMLDivElement>(null)
  const inKiesmodus = Boolean(kiesModus)
  const [gekozen, setGekozen] = useState<number[]>(
    () => (kiesModus?.begin?.items ?? []).map((it) => it.id))
  const [groot, setGroot] = useState<Item | null>(null)

  const [bericht, setBericht] = useState('')
  const [alsGetypt, setAlsGetypt] = useState(true)
  const [betaald, setBetaald] = useState(Boolean(kiesModus?.begin?.betaald))
  const [prijs, setPrijs] = useState(kiesModus?.begin?.prijs ?? '')
  // In kiesmodus kunnen gekozen items van een eerdere keer nog niet in de
  // geladen bladzijden staan; hun titel en soort onthouden we hier.
  const bekend = useRef(new Map<number, ContentKeuze['items'][number]>(
    (kiesModus?.begin?.items ?? []).map((it) => [it.id, it])))
  const [bezig, setBezig] = useState(false)
  const [stand, setStand] = useState('')
  // 18-09-2026 (Jordy): eerst zien (en een video kunnen afspelen) wat er
  // uitgaat, dan pas versturen. Dezelfde regel als bij Mass Messages: de
  // verzendknop blijft op slot tot de preview bekeken is, en elke wijziging
  // in de selectie, het bijschrift of de prijs maakt hem weer ongeldig.
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewGezien, setPreviewGezien] = useState(false)

  useEffect(() => {
    const h = window.setTimeout(() => setZoekTraag(zoek.trim()), 350)
    return () => window.clearTimeout(h)
  }, [zoek])

  useEffect(() => {
    api.get<Map_[]>(`/api/admin/content/folders?model_slug=${encodeURIComponent(modelSlug)}`)
      .then(setMappen)
      .catch(() => setMappen([]))
  }, [modelSlug])

  const url = useCallback((offset: number) => {
    const p = new URLSearchParams({
      model_slug: modelSlug,
      limit: String(PER_PAGINA),
      offset: String(offset),
    })
    if (conversationId != null) p.set('conversation_id', String(conversationId))
    if (map != null) p.set('folder_id', String(map))
    if (soort !== 'all') p.set('content_type', soort)
    if (zoekTraag) p.set('search', zoekTraag)
    return `/api/admin/content?${p.toString()}`
  }, [modelSlug, conversationId, map, soort, zoekTraag])

  /** Eén bladzijde ophalen, met de echte volgende offset van de server. */
  const vraag = useCallback(async (offset: number, gen: number): Promise<Bladzijde> => {
    const { data, volgende: vlg, meer: mr } =
      await getMetBladeren<Item[] | { data: Item[] }>(url(offset))
    const ruw = Array.isArray(data) ? data : (data?.data ?? [])
    // Kiesmodus (Mass DM): er is geen fan, dus "al naar deze fan" betekent
    // hier niets — nooit een item op slot zetten.
    const rij = inKiesmodus ? ruw.map((it) => ({ ...it, already_sent: false })) : ruw
    return {
      offset, gen, rij,
      // Een server zonder de headers (oude versie): val terug op het oude gedrag.
      volgende: vlg ?? offset + rij.length,
      meer: mr ?? rij.length === PER_PAGINA,
    }
  // 01-10-2026: hier stond `kiesModus` zelf als afhankelijkheid. Dat object is
  // bij elke render van het Mass DM-venster nieuw (de Chats-pagina ververst elke
  // 2,5 s), dus de kiezer laadde de bibliotheek steeds opnieuw en wiste de
  // selectie: "de kiezer flikkert". Alleen OF we in kiesmodus zijn telt.
  }, [url, inKiesmodus])

  /** De volgende bladzijde alvast ophalen en haar miniaturen opwarmen. */
  const zetKlaar = useCallback((offset: number, gen: number) => {
    window.setTimeout(() => {
      // Al opgehaald, al onderweg, of de chatter is er al voorbij: niets doen.
      if (gen !== generatie.current || volgendeRef.current !== offset
          || onderweg.current || voorraad.current?.offset === offset
          || (klaarzetten.current?.gen === gen && klaarzetten.current.offset === offset)) return
      const belofte = vraag(offset, gen)
        .then((bl) => {
          if (gen !== generatie.current) return null
          voorraad.current = bl
          // Opwarmen loopt door op de achtergrond; de bladzijde zelf is klaar.
          void warmBeelden(bl.rij.map((it) => thumbUrl(it.id)), WARM_TEGELIJK,
                           () => gen !== generatie.current)
          return bl
        })
        // Klaarzetten is een gemak; mislukt het, dan haalt scrollen het gewoon op.
        .catch(() => null)
      klaarzetten.current = { offset, gen, belofte }
    }, WARM_NA_MS)
  }, [vraag])

  const verwerk = useCallback((bl: Bladzijde) => {
    setItems((oud) => {
      if (bl.offset === 0) return bl.rij
      const bekend = new Set(oud.map((it) => it.id))
      return [...oud, ...bl.rij.filter((it) => !bekend.has(it.id))]
    })
    setVolgende(bl.volgende)
    volgendeRef.current = bl.volgende
    setMeer(bl.meer)
    if (bl.meer) zetKlaar(bl.volgende, bl.gen)
  }, [zetKlaar])

  const haal = useCallback(async (offset: number) => {
    const gen = generatie.current
    if (onderweg.current) return
    const klaar = voorraad.current
    if (klaar && klaar.gen === gen && klaar.offset === offset) {
      voorraad.current = null
      verwerk(klaar)
      return
    }
    onderweg.current = true
    setLaadt(true)
    try {
      // Wordt deze bladzijde al klaargezet? Dan daarop wachten, niet opnieuw vragen.
      const lopend = klaarzetten.current
      let bl: Bladzijde | null = null
      if (lopend && lopend.gen === gen && lopend.offset === offset) {
        bl = await lopend.belofte
        if (bl) voorraad.current = null
      }
      if (!bl) bl = await vraag(offset, gen)
      if (gen === generatie.current) verwerk(bl)
    } catch (fout) {
      if (gen === generatie.current) {
        notify(`Could not load the library: ${fout instanceof Error ? fout.message : fout}`,
               false)
      }
    } finally {
      onderweg.current = false
      if (gen === generatie.current) setLaadt(false)
    }
  }, [vraag, verwerk, notify])

  /** Vanaf het begin: bij openen, bij elke filterwissel en bij Refresh. */
  const opnieuw = useCallback(() => {
    generatie.current += 1
    voorraad.current = null
    klaarzetten.current = null
    volgendeRef.current = 0
    onderweg.current = false
    setItems([])
    setVolgende(0)
    setMeer(false)
    raster.current?.scrollTo({ top: 0 })
    haal(0)
  }, [haal])

  // Een filterwissel begint een nieuwe lijst en wist de selectie. Bij het
  // OPENEN niet: in kiesmodus (Mass DM, "Change content") staat daar de
  // eerdere keuze al in (01-10-2026). In de chat is hij dan toch leeg.
  const eersteLading = useRef(true)
  useEffect(() => {
    if (eersteLading.current) eersteLading.current = false
    else setGekozen([])
    opnieuw()
  }, [opnieuw])

  // Automatisch de volgende bladzijde zodra het einde van het raster nadert.
  useBijRand(wacht, raster, () => { if (meer && !laadt) haal(volgende) },
             meer && !laadt, '300px 0px')

  /** 17-09-2026 (Jordy, HERSTEL-007): dezelfde foto mag niet twee keer naar
   *  dezelfde fan. Eerder was dit bewust alleen een markering; nu is het een
   *  slot — al verstuurde content is niet te kiezen. */
  const alVerstuurd = (id: number) =>
    items.some((it) => it.id === id && it.already_sent)
  const kies = (id: number) => {
    if (alVerstuurd(id)) return
    setPreviewGezien(false)
    setGekozen((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  }

  /** De grens die het platform stelt; onder dit bedrag weigert het de bijlage. */
  const minimum = kiesModus
    ? (f2f ? MASS_PPV_MIN_F2F_EUR : MASS_PPV_MIN_FC_CREDITS)
    : (f2f ? PPV_MIN_F2F_EUR : PPV_MIN_FC_CREDITS)
  // 11-10-2026 (Jordy): leeg of onder het minimum → een duidelijke melding
  // (eenheid, minimum, omrekening) en de knop blijft uit. Zie lib/massPrijs.ts.
  const prijsFout = useMemo(
    () => (betaald ? kiezerPrijsFout(platform, prijs, inKiesmodus) : null),
    [betaald, prijs, platform, inKiesmodus])

  /** 02-10-2026 (Jordy): de prijs op de knoppen in de eenheid van het platform —
   *  F2F "€15", FanCentro "4,000 credits" (was "4000 credits", ongegroepeerd). */
  const prijsTekst = f2f ? `€${String(prijs || 0)}` : credits(prijs || 0)
  /** 18-10-2026 (Jordy): de knoptekst zegt altijd precies wat er gebeurt —
   *  "Send (Free)" of "Send (Paid · €5 / 500 credits)"; nooit een knop die
   *  "Free" heet en Paid maakt. */
  const knopTekst = (werkwoord: string) => {
    const n = gekozen.length
    const stand = betaald && magBetaald
      ? `(Paid · ${String(prijs).trim() ? prijsTekst : 'set a price'})`
      : '(Free)'
    return `${werkwoord} ${stand}${n > 1 ? ` · ${n} items` : ''}`
  }
  /** Free/Paid kiezen. Bij Paid in kiesmodus (Mass DM) is de prijs uit de
   *  bibliotheek een voorstel, alleen als het veld nog leeg is (04-10-2026). */
  const kiesBetaald = (aan: boolean) => {
    if (aan && kiesModus && !betaald && !String(prijs).trim()) {
      const voorstel = bibliotheekVoorstel(gekozen.map((id) =>
        items.find((x) => x.id === id) ?? bekend.current.get(id) ?? {}), platform)
      if (voorstel != null) setPrijs(String(voorstel))
    }
    setPreviewGezien(false)
    setBetaald(aan)
  }

  const maxItems = kiesModus ? kiesModus.max : f2f ? 6 : 20
  const teVeel = gekozen.length > maxItems
  const magBetaald = kiesModus ? kiesModus.magBetaald !== false : true

  /** Kiesmodus: de selectie teruggeven aan het Mass DM-venster. */
  function gebruik() {
    if (!kiesModus || !gekozen.length || prijsFout || teVeel) return
    const keuzeItems = gekozen.map((id) => {
      const it = items.find((x) => x.id === id)
      return it
        ? { id: it.id, title: it.title, content_type: it.content_type ?? null, base_price: it.base_price ?? null }
        : bekend.current.get(id) ?? { id, title: `#${id}`, content_type: null }
    })
    kiesModus.onKies({ items: keuzeItems, betaald: magBetaald && betaald, prijs: betaald ? prijs : '' })
    onClose()
  }

  async function verstuur() {
    // Dubbele bodem: mocht er ooit langs de kiezer toch iets in de selectie
    // komen dat al naar deze fan ging, dan gaat het hier alsnog niet mee.
    const teSturen = gekozen.filter((id) => !alVerstuurd(id))
    if (!teSturen.length || prijsFout || teVeel) return
    const bedrag = betaald ? Number(String(prijs).replace(',', '.')) : 0
    // 11-09-2026 (Jordy): hier stond een `window.confirm` — "1 item(s) gratis
    // versturen?" — bovenop een venster waar je net bewust op Send hebt
    // geklikt. Een browserpopup die herhaalt wat er al op het scherm staat
    // voegt niets toe; je klikt hem weg zonder te lezen, en dan is hij ook
    // geen rem meer.
    //
    // Wat hij WEL waard was, staat nu in de knop zelf: die zegt of het gratis
    // gaat of wat het kost. De informatie blijft, de onderbreking gaat weg.
    setBezig(true)
    try {
      const taak = await api.post<Taak>(
        `/api/admin/conversations/${conversationId}/send-library-content`, {
          content_item_ids: teSturen,
          caption: bericht,
          is_ppv: betaald,
          ppv_price: bedrag,
          client_request_id: `cp-${conversationId}-${Date.now()}-${Math.random()
            .toString(36).slice(2, 10)}`,
          // 15-10-2026: op F2F beslist de server (altijd vertalen volgens de fan).
          ...(f2f ? {} : { translation_enabled: !alsGetypt }),
        })
      // 17-09-2026 (Jordy: "2 item(s) queued but nothing happens"). Hier stond
      // alleen die melding, waarna het venster sloot. "Queued" betekent precies
      // dat: de server heeft de opdracht aangenomen. Mislukt hij een tel later
      // — en dat gebeurde, zie hieronder — dan zag de chatter dat NERGENS: geen
      // fout, geen bericht in het gesprek, niets. Daarom volgen we de opdracht
      // nu tot ze klaar is en zeggen we wat eruit kwam.
      setStand('Sending to the fan…')
      const uit = await volgTaak(taak?.id)
      if (uit?.status === 'failed') {
        notify(uit.error || 'The send failed.', false)
        return
      }
      if (uit?.status === 'succeeded') {
        notify(`${teSturen.length} item(s) sent.`)
      } else {
        // Nog bezig: dat mag, een grote video duurt. Het gesprek toont hem
        // zodra de worker klaar is.
        notify(`${teSturen.length} item(s) still sending in the background.`)
      }
      onVerstuurd?.()
      onClose()
    } catch (fout) {
      notify(`Send failed: ${fout instanceof Error ? fout.message : fout}`, false)
    } finally {
      setBezig(false)
      setStand('')
    }
  }

  /** Wacht tot de verzendopdracht klaar is. Geeft de laatste stand terug, ook
   *  als die "nog bezig" is: dan blijft de worker gewoon doorwerken. */
  async function volgTaak(id: string | undefined): Promise<Taak | null> {
    if (!id) return null
    const eind = Date.now() + VOLG_MAX_MS
    let laatste: Taak | null = null
    while (Date.now() < eind) {
      await new Promise((r) => setTimeout(r, VOLG_TIK_MS))
      try {
        const rijen = await api.get<Taak[]>(
          '/api/admin/conversations/media-send-jobs/mine'
          + `?conversation_id=${conversationId}&include_acknowledged=true&limit=50`)
        const taak = (rijen ?? []).find((r) => r.id === id) ?? null
        if (taak) laatste = taak
        if (taak && (taak.status === 'succeeded' || taak.status === 'failed')) return taak
      } catch {
        // Een mislukte peiling zegt niets over de verzending zelf; gewoon
        // opnieuw proberen tot de tijd om is.
      }
    }
    return laatste
  }

  return (
    <Modal
      title={kiesModus ? 'Add content to the Mass DM' : 'Send from Content Library'}
      desc={kiesModus
        ? `Pick up to ${kiesModus.max} photos or videos for this model, free or paid. Nothing is sent from here.`
        : 'Pick pre-approved content for this model. The send continues in the background, so you can move to another chat after it is queued.'}
      width={900}
      onClose={onClose}
      footer={kiesModus ? (
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Close</button>
          <button className="btn ghost sm" type="button"
                  disabled={!gekozen.length}
                  onClick={() => setGekozen([])}>
            Deselect
          </button>
          <button className="btn primary sm" type="button"
                  disabled={!gekozen.length || !!prijsFout || teVeel}
                  onClick={gebruik}>
            {!gekozen.length ? 'Use selection' : knopTekst('Use')}
          </button>
        </>
      ) : (
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Close</button>
          <button className="btn ghost sm" type="button"
                  disabled={!gekozen.length}
                  onClick={() => setGekozen([])}>
            Deselect
          </button>
          {/* 18-09-2026: eerst kijken (en een video echt afspelen), dan pas
              versturen — dezelfde regel als bij Mass Messages. */}
          <button className="btn sm" type="button"
                  disabled={!gekozen.length}
                  onClick={() => setPreviewOpen(true)}>
            Preview
          </button>
          {/* 23-09-2026 (Jordy, tijdens de PPV-test: "ik kan niet meer drukken
              op send"). Deze knop stond UIT tot de preview bekeken was, maar
              een uitgeschakelde knop zag er precies zo uit als een gewone:
              hij oogde klikbaar, deed niets, en er kwam geen melding. Nooit
              meer doodlopen — is de preview nog niet gezien, dan OPENT deze
              knop de preview, en vanuit dat venster verstuur je. */}
          <button className="btn primary sm" type="button"
                  disabled={!gekozen.length || !!prijsFout || teVeel || bezig}
                  title={previewGezien
                    ? undefined
                    : 'Shows the preview first — you send from there'}
                  onClick={() => {
                    if (!previewGezien) { setPreviewOpen(true); return }
                    verstuur()
                  }}>
            {bezig ? 'Sending…' : !gekozen.length ? 'Send' : knopTekst('Send')}
          </button>
        </>
      )}
    >
      <div className="filters" style={{ marginBottom: 10 }}>
        <span className="mut2" style={{ fontSize: 12 }}>Filter:</span>
        <div className="seg">
          {SOORTEN.map((s) => (
            <button key={s.value} type="button"
                    className={soort === s.value ? 'on' : undefined}
                    onClick={() => setSoort(s.value)}>
              {s.label}
            </button>
          ))}
        </div>
        <button className="btn ghost sm" type="button" onClick={opnieuw}>
          Refresh
        </button>
      </div>

      <div className="filters" style={{ marginBottom: 10 }} data-tour="content-filter">
        <span className="mut2" style={{ fontSize: 12 }}>Folder</span>
        <select className="select" value={map ?? ''}
                onChange={(e) => setMap(e.target.value ? Number(e.target.value) : null)}>
          <option value="">All folders</option>
          {mappen.map((m) => (
            <option key={m.id} value={m.id}>{m.path} ({m.item_count})</option>
          ))}
        </select>
        <input className="inp" style={{ maxWidth: 240 }}
               placeholder="Search title / tags…"
               value={zoek} onChange={(e) => setZoek(e.target.value)} />
        <span className="spacer" style={{ flex: 1 }} />
        <span className="mut2" style={{ fontSize: 12 }}>{num(items.length)} items</span>
      </div>

      <div className="kiezergrid" ref={raster} data-tour="content-bundle">
        {items.map((it) => {
          const aan = gekozen.includes(it.id)
          return (
            <div key={it.id}
                 className={
                   it.already_sent ? 'kiezerkaart vergrendeld'
                     : aan ? 'kiezerkaart aan' : 'kiezerkaart'
                 }
                 title={it.already_sent
                   ? 'Already sent to this fan — cannot be sent again'
                   : undefined}
                 onClick={() => kies(it.id)}>
              {/* 17-09-2026 (Jordy): een bestand dat al naar DEZE fan is
                  gegaan, staat vervaagd met een label erop. Zo gaat dezelfde
                  foto niet twee keer naar dezelfde fan. Het is een markering,
                  geen slot: aanklikken en toch versturen kan gewoon. */}
              <div className={it.already_sent ? 'beeld alverstuurd' : 'beeld'}>
                <KiezerBeeld id={it.id} titel={it.title} raster={raster} />
                {it.already_sent ? (
                  <span className="alverstuurd-label">Already sent to this fan</span>
                ) : null}
                <button type="button" className="oog" title="Preview"
                        onClick={(e) => { e.stopPropagation(); setGroot(it) }}>
                  👁
                </button>
                <input type="checkbox" className="vink" checked={aan}
                       disabled={it.already_sent}
                       onClick={(e) => e.stopPropagation()}
                       onChange={() => kies(it.id)} />
              </div>
              <div className="onder">
                <div className="titel" title={it.title}>{it.title}</div>
                <div className="pad mut2">
                  {mapPad(it.file_reference)}
                  {String(it.content_type).toLowerCase().startsWith('video') && duurLabel(it.duration_seconds)
                    ? <span className="mono" title="Video length"> · {duurLabel(it.duration_seconds)}</span>
                    : null}
                </div>
                <Tag kind={it.base_price > 0 ? 'acc' : 'mut'}>
                  {it.base_price > 0 ? 'paid' : 'free'}
                </Tag>
                {it.already_sent ? <Tag kind="warn">sent to this fan</Tag> : null}
              </div>
            </div>
          )
        })}
        {/* Het einde van het raster: komt dit in de buurt, dan laadt de
            volgende bladzijde vanzelf (30-09-2026 — geen knop meer). */}
        <div ref={wacht} className="kiezerwacht" style={{ gridColumn: '1 / -1' }}>
          {laadt && items.length ? <Loading /> : null}
          {!meer && items.length > PER_PAGINA ? (
            <span className="mut2" style={{ fontSize: 12 }}>End of the library.</span>
          ) : null}
        </div>
      </div>

      {laadt && !items.length ? <Loading /> : null}
      {!laadt && !items.length ? (
        <div className="mut2" style={{ textAlign: 'center', padding: 20, fontSize: 12 }}>
          No content in this folder.
        </div>
      ) : null}

      {/* ---- wat er meegaat ---- */}
      <div className="divh" style={{ margin: '12px 0' }} />
      {kiesModus ? null : (<>
      <div className="row" style={{ gap: 8, marginBottom: 8 }}>
        <input className="inp" style={{ flex: 1 }}
               placeholder="Add a message to send with this content (optional)…"
               value={bericht}
               onChange={(e) => { setPreviewGezien(false); setBericht(e.target.value) }} />
        {/* 17-09-2026 (Jordy): hier zat een knop die altijd dezelfde 🙂 plakte,
            zonder menu. Nu dezelfde keuze als in het typvak van de chat. */}
        <EmojiKnop uitlijnen="rechts"
                   onKies={(e) => { setPreviewGezien(false); setBericht((b) => b + e) }} />
      </div>

      </>)}
      <div className="row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {kiesModus ? null : f2f ? (
          <span className="mut2" style={{ fontSize: 11.5 }} data-testid="f2f-bijschrift-vast">
            F2F: translations are always on — the caption goes out in the fan’s language
            (Dutch for a Dutch fan, otherwise as typed).
          </span>
        ) : (<>
        <label className="chkrow">
          <span className={alsGetypt ? 'chk on' : 'chk'}
                onClick={() => setAlsGetypt((v) => !v)}>
            {alsGetypt ? '✓' : ''}
          </span>
          Send caption as typed
        </label>
        <span className="mut2" style={{ fontSize: 11.5 }}>
          {alsGetypt
            ? 'Caption goes out exactly as typed.'
            : 'Caption is translated to the fan’s language first.'}
        </span>
        </>)}

        <span className="spacer" style={{ flex: 1 }} />

        {/* In kiesmodus zonder recht op betaalde Mass DM: alleen gratis. */}
        {magBetaald ? (<>
        {/* 18-10-2026 (Jordy): twee standen, de actieve gemarkeerd (zoals bij
            Mass DM); niet meer één knop die "Free" heet en Paid maakt. */}
        <div className="seg betaalkeuze" role="radiogroup" aria-label="Free or paid"
             data-testid="betaalkeuze">
          <button type="button" role="radio" aria-checked={!betaald}
                  className={betaald ? undefined : 'on'} data-testid="keuze-free"
                  onClick={() => kiesBetaald(false)}>
            🔓 Free
          </button>
          <button type="button" role="radio" aria-checked={betaald}
                  className={betaald ? 'on' : undefined} data-testid="keuze-paid"
                  onClick={() => kiesBetaald(true)}>
            🔒 Paid
          </button>
        </div>
        {betaald ? (
          // 11-10-2026 (Jordy): de eenheid staat zichtbaar naast het veld.
          <span className="row" style={{ gap: 6, alignItems: 'center' }}>
            {f2f ? <span className="mut2" data-testid="prijs-eenheid">€</span> : null}
            <input className="inp prijsveld" type="number" min={minimum} required
                   step={f2f ? '0.01' : '1'} data-testid="prijsveld"
                   aria-label={f2f ? 'Price in euro' : 'Price in credits'}
                   placeholder={`min ${minimum}`}
                   value={prijs}
                   onChange={(e) => { setPreviewGezien(false); setPrijs(e.target.value) }} />
            {f2f ? null : <span className="mut2" data-testid="prijs-eenheid">credits</span>}
          </span>
        ) : null}
        </>) : (
          <span className="mut2" style={{ fontSize: 12 }}>Free only — paid Mass DMs aren’t enabled for your account.</span>
        )}
      </div>

      {/* 11-10-2026 (Jordy): bij "Paid" altijd de uitleg: eenheid, omrekening
          en minimum. Daaronder, als het niet klopt, wat er mis is. */}
      {magBetaald && betaald ? (
        <div className="mut2" data-testid="prijs-hulp" style={{ marginTop: 8, fontSize: 12 }}>
          {prijsHulpTekst(platform)}
        </div>
      ) : null}
      {prijsFout ? (
        <div className="note" data-testid="prijs-fout" role="alert"
             style={{ marginTop: 8, color: '#fca5a5' }}>{prijsFout}</div>
      ) : null}
      {teVeel ? (
        <div className="note" style={{ marginTop: 8, color: '#fca5a5' }}>
          {kiesModus
            ? `A Mass DM carries at most ${kiesModus.max} items.`
            : f2f
              ? 'An F2F bundle holds at most 6 items.'
              : 'A send holds at most 20 items.'}{' '}
          Selected now: {gekozen.length}.
        </div>
      ) : null}
      {gekozen.length && !teVeel && !prijsFout ? (
        <div className="mut2" style={{ marginTop: 8, fontSize: 12 }}>
          {gekozen.length} selected · goes out{' '}
          {betaald
            ? (f2f ? `for €${prijs || '—'}` : `for ${prijs ? credits(prijs) : '— credits'}`)
            : 'free'}
          {gekozen.length > 1 ? (
            // 17-09-2026: een selectie van meerdere bestanden gaat als ÉÉN
            // bundel naar de server, en die verzending mislukt op dit moment
            // (zie de melding die je dan krijgt). Eén tegelijk werkt wel.
            <span style={{ color: '#fbbf24' }}>
              {' '}· as one bundle — if that fails, send them one by one
            </span>
          ) : null}
        </div>
      ) : null}
      {stand ? (
        <div className="mut2" style={{ marginTop: 8, fontSize: 12 }}>{stand}</div>
      ) : null}

      {previewOpen ? (
        <VerzendPreview
          items={gekozen
            .map((id) => items.find((it) => it.id === id))
            .filter((it): it is Item => Boolean(it))
            .map((it): PreviewItem => ({
              id: it.id,
              titel: it.title,
              soort: String(it.content_type || '').toLowerCase(),
              // Het echte bestand, niet de miniatuur: anders kun je een video
              // niet afspelen en zie je een foto niet op ware grootte.
              url: fileUrl(it.id),
            }))}
          bijschrift={bericht}
          betaald={betaald}
          prijs={prijs}
          f2f={f2f}
          onSluit={() => { setPreviewGezien(true); setPreviewOpen(false) }}
          onBevestig={() => {
            setPreviewGezien(true)
            setPreviewOpen(false)
            verstuur()
          }}
          bevestigTekst={knopTekst('Send')}
        />
      ) : null}

      {groot ? (
        <div className="lichtbak" onClick={() => setGroot(null)}>
          <div className="inhoud" onClick={(e) => e.stopPropagation()}>
            {groot.content_type === 'video'
              ? <video src={fileUrl(groot.id)} controls autoPlay />
              : <img src={fileUrl(groot.id)} alt={groot.title} />}
            <div className="bijschrift">
              <b>{groot.title}</b>
              <span className="mut2">{mapPad(groot.file_reference)}</span>
              <button className="btn ghost sm" type="button"
                      onClick={() => setGroot(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
